"use client";

/**
 * Radar (spider/hexagon) chart core - pure SVG, no chart dependency.
 *
 * Renders N series as translucent polygons over a shared 0..100 grid
 * with axis labels. Owns only geometry and paint; every surface that
 * uses it (CompareRadar's multi-listing overlay, PropertyRadar's
 * single-listing mini) supplies its own normalized values, so scale
 * semantics live with the data, not here.
 */

import { useState } from "react";

export interface RadarSeries {
  /** Stroke/fill color (hex - inline SVG paint). */
  hex: string;
  /** One 0..100 value per axis, same order as `labels`. */
  values: number[];
}

const R = 100;
const LABEL_R = 116;

function point(
  axisIdx: number,
  axisCount: number,
  radius: number,
): [number, number] {
  const angle = (Math.PI * 2 * axisIdx) / axisCount - Math.PI / 2;
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
}

function polygonPath(radii: number[]): string {
  const n = radii.length;
  return radii
    .map((r, i) => {
      const [x, y] = point(i, n, r);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ") + " Z";
}

export default function RadarChart({
  labels,
  series,
  labelFontSize = 11,
  className = "",
  ariaLabel,
}: {
  labels: string[];
  series: RadarSeries[];
  labelFontSize?: number;
  className?: string;
  ariaLabel?: string;
}) {
  // Hover/tap highlight: the hovered series pops (denser fill, thicker
  // stroke) while the others recede - with 3-4 overlapping polygons
  // that's the only way to actually READ one listing's shape.
  const [active, setActive] = useState<number | null>(null);
  const n = labels.length;
  if (n < 3 || series.length === 0) return null;
  // Enough viewBox padding that side labels never clip.
  const view = 380;

  return (
    <svg
      viewBox={`${-view / 2} ${-view / 2} ${view} ${view}`}
      className={`block h-auto w-full ${className}`}
      role="img"
      aria-label={ariaLabel}
    >
      {[25, 50, 75, 100].map((ring) => (
        <path
          key={ring}
          d={polygonPath(labels.map(() => (ring / 100) * R))}
          fill="none"
          className="stroke-gray-200"
          strokeWidth="1"
        />
      ))}
      {labels.map((_, i) => {
        const [x, y] = point(i, n, R);
        return (
          <line
            key={i}
            x1="0"
            y1="0"
            x2={x.toFixed(1)}
            y2={y.toFixed(1)}
            className="stroke-gray-200"
            strokeWidth="1"
          />
        );
      })}

      {series.map((s, si) => {
        const d = polygonPath(
          s.values.map((v) => (Math.max(0, Math.min(100, v)) / 100) * R),
        );
        const dimmed = active != null && active !== si;
        const lifted = active === si;
        return (
          <g
            key={si}
            className="radar-in"
            style={{
              animationDelay: `${si * 120}ms`,
              opacity: dimmed ? 0.25 : undefined,
              transition: "opacity 150ms ease-out",
            }}
            onMouseEnter={() => setActive(si)}
            onMouseLeave={() => setActive(null)}
            onClick={() => setActive((a) => (a === si ? null : si))}
          >
            {/* Invisible fat hit area so hover works inside the whole
                polygon, not just on the 2px stroke. */}
            <path d={d} fill={s.hex} fillOpacity={lifted ? 0.32 : 0.18} style={{ transition: "fill-opacity 150ms ease-out" }} />
            <path
              d={d}
              fill="none"
              stroke={s.hex}
              strokeWidth={lifted ? 3 : 2}
              strokeLinejoin="round"
            />
            {s.values.map((v, ai) => {
              const [x, y] = point(
                ai, n, (Math.max(0, Math.min(100, v)) / 100) * R,
              );
              return (
                <circle
                  key={ai}
                  cx={x.toFixed(1)}
                  cy={y.toFixed(1)}
                  r={lifted ? 3.5 : 2.5}
                  fill={s.hex}
                />
              );
            })}
          </g>
        );
      })}

      {labels.map((label, i) => {
        const [x, y] = point(i, n, LABEL_R);
        const anchor = Math.abs(x) < 10 ? "middle" : x > 0 ? "start" : "end";
        return (
          <text
            key={i}
            x={x.toFixed(1)}
            y={(y + 3).toFixed(1)}
            textAnchor={anchor}
            fontSize={labelFontSize}
            fontWeight="600"
            className="fill-gray-500"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}
